<template>
  <section id="Documents">
    <!-- No company selected: hide the entire documents UI (search box,
         My Machines / All Documents toggle, and the table) and show the
         shared select-a-company prompt instead. Documents are scoped to
         the selected company, so there's nothing meaningful to show
         without one. -->
    <div class="container" v-if="!companySelected">
      <select-company-prompt :user-name="currentUser && currentUser.firstName ? currentUser.firstName : 'Admin'" />
    </div>

    <div id="acctOrders" v-if="companySelected">
      <div class="container">
        <!--
          Two-column layout: documents table on the left, shared
          CartWidget (standalone mode) on the right. Same pattern
          as SavedCarts / OrderHistory — cart column collapses to
          zero-width (oh-cart-col-hidden) when the widget signals
          its inline tray isn't visible (cart empty, or user has
          collapsed to side pill), and the left column stretches
          back to col-12 so the table fills the row.
        -->
        <div class="row">
          <div class="col-12">
        <div id="ao-table">
          <!-- Controls Row -->
          <div class="row align-items-center mb-4">
            <div class="col-lg-5 mb-3 mb-lg-0">
              <div class="input-group">
                <input
                  type="text"
                  class="form-control"
                  v-model="searchTerm"
                  placeholder="Search documents by name..."
                  @keyup.enter="fetchDocuments"
                />
                <div class="input-group-append">
                  <button
                    class="doc-search-btn"
                    type="button"
                    @click="fetchDocuments"
                  >
                    <span class="fa fa-search"></span> Search
                  </button>
                </div>
              </div>
            </div>

            <div class="col-lg-4 mb-3 mb-lg-0">
              <div class="doc-toggle" role="group">
                <button
                  type="button"
                  class="doc-toggle-btn"
                  :class="{ 'is-active': showMyMachines }"
                  @click="toggleView(true)"
                >
                  My Machines
                </button>
                <button
                  type="button"
                  class="doc-toggle-btn"
                  :class="{ 'is-active': !showMyMachines }"
                  @click="toggleView(false)"
                >
                  All Documents
                </button>
              </div>
            </div>

            <div class="col-lg-3 text-right" v-if="documents.length > 0">
              <span class="text-muted">{{ documents.length }} document{{ documents.length !== 1 ? 's' : '' }} found</span>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-3 text-muted">Loading documents...</p>
          </div>

          <!-- No Results -->
          <div v-else-if="documents.length === 0" class="row">
            <div class="col-12 mt-4">
              <h3>No documents found.</h3>
              <p v-if="searchTerm" class="text-muted">
                Try a different search term or
                <a href="#" @click.prevent="clearSearch">clear the search</a>.
              </p>
              <p v-else-if="showMyMachines" class="text-muted">
                No documents are available for your machines. Try switching to
                <a href="#" @click.prevent="toggleView(false)">All Documents</a>.
              </p>
            </div>
          </div>

          <!-- Documents Table -->
          <b-table
            v-else
            id="documentsTable"
            :per-page="perPage"
            :current-page="currentPage"
            stacked="md"
            :fields="tableFields"
            :items="documents"
            responsive="sm"
            hover
          >
            <!-- Expand toggle — only shown for documents that have match
                 reasons (the My Machines view). Reveals the "why suggested"
                 detail panel below the row. -->
            <template v-slot:cell(expand)="row">
              <button v-if="row.item.matchReasons && row.item.matchReasons.length"
                      type="button"
                      class="doc-expand-btn"
                      :class="{ 'is-open': row.detailsShowing }"
                      :title="row.detailsShowing ? 'Hide details' : 'Why is this suggested?'"
                      :aria-label="row.detailsShowing ? 'Hide details' : 'Why is this suggested?'"
                      @click="row.toggleDetails">
                {{ row.detailsShowing ? '−' : '+' }}
              </button>
            </template>

            <!-- Name Column -->
            <template v-slot:cell(name)="data">
              <span class="fa fa-file-pdf-o text-danger mr-2"></span>
              <strong>{{ formatName(data.item.name) }}</strong>
            </template>

            <!-- Category Column -->
            <template v-slot:cell(category)="data">
              <span class="badge" :class="categoryBadgeClass(data.item.category)">
                {{ data.item.category }}
              </span>
            </template>

            <!-- Size Column -->
            <template v-slot:cell(size)="data">
              {{ formatSize(data.item.size) }}
            </template>

            <!-- Last Modified Column -->
            <template v-slot:cell(lastModified)="data">
              {{ data.item.lastModified | formatDate }}
            </template>

            <!-- Download Column -->
            <template v-slot:cell(download)="data">
              <a
                :href="data.item.url"
                target="_blank"
                class="doc-pdf-link"
                rel="noopener noreferrer"
              >
                <svg class="cart-icon-svg"
                     xmlns="http://www.w3.org/2000/svg"
                     viewBox="0 0 576 512"
                     width="16" height="16">
                  <path fill="#c0392b"
                    d="M572.5 241.4C518.3 135.6 410.9 64 288 64S57.7 135.6 3.5 241.4a32.4 32.4 0 0 0 0 29.2C57.7 376.4 165.1 448 288 448s230.3-71.6 284.5-177.4a32.4 32.4 0 0 0 0-29.2zM288 400a144 144 0 1 1 144-144 143.9 143.9 0 0 1 -144 144zm0-240a95.3 95.3 0 0 0 -25.3 3.8 47.9 47.9 0 0 1 -66.9 66.9A95.8 95.8 0 1 0 288 160z"/>
                </svg>
                View PDF
              </a>
            </template>

            <!-- "Why suggested" detail panel — lists the machines / parts the
                 customer owns or purchased whose name matched this document,
                 with the purchase date. -->
            <template v-slot:row-details="row">
              <div class="doc-why">
                <div class="doc-why-title">Why this document is suggested</div>
                <ul class="doc-why-list">
                  <li v-for="(r, i) in row.item.matchReasons"
                      :key="i"
                      class="doc-why-item">
                    <span class="doc-why-kind"
                          :class="'kind-' + (r.kind || '').toLowerCase()">{{ r.kind }}</span>
                    <span class="doc-why-label">{{ r.label }}</span>
                    <span class="doc-why-date" v-if="r.purchaseDate">
                      — purchased {{ formatDateShort(r.purchaseDate) }}
                    </span>
                    <span class="doc-why-date doc-why-onfile" v-else>
                      — on file for your company
                    </span>
                  </li>
                </ul>
              </div>
            </template>
          </b-table>

          <!-- Pagination -->
          <b-pagination
            v-if="documents.length > perPage"
            v-model="currentPage"
            :total-rows="documents.length"
            :per-page="perPage"
            aria-controls="documentsTable"
          ></b-pagination>
        </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import { recordSearch } from "@/assets/js/activity";
import FeApiService from "@/assets/js/FeApiService";
import SelectCompanyPrompt from "@/components/Common/SelectCompanyPrompt.vue";
const apiService = new FeApiService();

export default {
  name: "Documents",
  components: { SelectCompanyPrompt },
  computed: {
    // Pull the signed-in user from Vuex the same way the dashboard does,
    // so the select-a-company prompt can greet them by first name.
    ...mapGetters({ currentUser: "currentUser" }),
  },
  mounted() {
    this.currentCompanyId = this.resolveCompanyId();
    this.companySelected = !!this.currentCompanyId;
    this.fetchDocuments();
    // The header company dropdown updates localStorage.companyId without a
    // full page reload, so poll for changes and refetch — mirrors the
    // companyCheckInterval pattern in SavedCarts.vue.
    this.companyCheckInterval = setInterval(() => {
      const newCompanyId = this.resolveCompanyId();
      if (newCompanyId !== this.currentCompanyId) {
        this.currentCompanyId = newCompanyId;
        this.companySelected = !!newCompanyId;
        this.fetchDocuments();
      }
    }, 500);
  },
  beforeDestroy() {
    if (this.companyCheckInterval) {
      clearInterval(this.companyCheckInterval);
    }
  },
  data() {
    return {
      searchTerm: "",
      showMyMachines: true,
      loading: false,
      documents: [],
      currentCompanyId: "",
      companySelected: false,
      companyCheckInterval: null,
      currentPage: 1,
      perPage: 15,
      tableFields: [
        { key: "expand", label: "", thClass: "doc-expand-th", tdClass: "doc-expand-td" },
        { key: "name", label: "Document Name", sortable: true },
        { key: "category", label: "Category", sortable: true },
        { key: "size", label: "File Size", sortable: true },
        { key: "lastModified", label: "Date Added", sortable: true },
        { key: "download", label: "" },
      ],
    };
  },
  methods: {

    async fetchDocuments() {
      this.loading = true;
      this.currentPage = 1;
      // Activity trail: the single document-search funnel (button, Enter, clear, toggle).
      recordSearch("documents", this.searchTerm);
      try {
        if (this.showMyMachines) {
          this.documents = await apiService.getMyDocuments(this.searchTerm, this.resolveCompanyId());
        } else {
          this.documents = await apiService.getDocuments(this.searchTerm);
        }
      } catch (error) {
        console.error("Error fetching documents:", error);
        this.documents = [];
      }
      this.loading = false;
    },

    toggleView(myMachines) {
      if (this.showMyMachines !== myMachines) {
        this.showMyMachines = myMachines;
        this.fetchDocuments();
      }
    },

    clearSearch() {
      this.searchTerm = "";
      this.fetchDocuments();
    },

    // Resolve the currently-selected company id (the header dropdown writes
    // it to localStorage). Passed to the my-machines endpoint so documents
    // filter to the selected customer's machines + purchase history.
    resolveCompanyId() {
      const ls = localStorage.companyId;
      if (ls) return String(ls);
      if (this.$store && this.$store.state) {
        const s = this.$store.state;
        const cid = s.companyId || s.currentCompanyId || s.selectedCompanyId;
        if (cid) return String(cid);
      }
      return "";
    },

    formatName(filename) {
      // Remove .pdf extension and replace hyphens/underscores with spaces
      return filename
        .replace(/\.pdf$/i, "")
        .replace(/[-_]/g, " ");
    },

    formatSize(bytes) {
      if (bytes === 0) return "0 B";
      const k = 1024;
      const sizes = ["B", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
    },

    // Date-only formatting for the "purchased on" line in the why-suggested
    // panel (the backend sends a full timestamp; the time isn't useful here).
    formatDateShort(value) {
      if (!value) return "";
      const dt = new Date(value);
      if (isNaN(dt.getTime())) return "";
      const mm = String(dt.getMonth() + 1).padStart(2, "0");
      const dd = String(dt.getDate()).padStart(2, "0");
      return mm + "/" + dd + "/" + dt.getFullYear();
    },

    categoryBadgeClass(category) {
      switch (category) {
        case "Lube Chart":
          return "badge-primary";
        case "Datasheet":
          return "badge-info";
        case "Reference Drawing":
          return "badge-warning";
        case "Manual":
          return "badge-success";
        case "Specification":
          return "badge-secondary";
        case "Parts List":
          return "badge-dark";
        default:
          return "badge-light";
      }
    },
  },
};
</script>

<style scoped>
/* ===== MAIN CONTENT CARD — RED TOP STRIPE =====
   Other portal pages (SavedCarts, OrderHistory, Account dashboard,
   Cart success, etc.) all render their primary content card with
   a 3px brand-red stripe along the top edge. Documents was missing
   this — its #ao-table got the white background, rounded corners,
   and shadow from the global `#Documents > .container` selector in
   steele-consistency-fix.css section 12, but no stripe.

   The fix: a 3px brand-red top border on #ao-table (the inner white
   card). A plain `#ao-table` rule wasn't enough — the card inherits a
   1px solid #ddd top border from a higher-specificity panel rule, which
   was winning and hiding the stripe (measured: border-top was 1px #ddd,
   not the red 3px). So we target `#acctOrders #ao-table` (two ids + the
   scoped data-v attribute) WITH !important to override it — the same fix
   used on SavedCarts.vue.

   Color #c0392b matches the same token used by every other red top
   stripe in the codebase (CartWidget, Account dashboard, OrderDetails,
   Cart/Success, ShopAssembly, ShopWearParts).

   Placement: #ao-table specifically (the inner card), not the outer
   #acctOrders or .container, so the stripe sits at the top of the
   visible white panel rather than offset by any container padding. */
#acctOrders #ao-table {
  border-top: 3px solid #c0392b !important;
}

.badge {
  font-size: 0.85em;
  padding: 5px 10px;
}
.input-group .form-control {
  height: 42px;
}

/* ===== MODERN CONTROLS — match the SavedCarts look ===== */
/* Search: solid brand-red button flush to the input's right edge. */
.doc-search-btn {
  height: 42px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #c0392b;
  border: none;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  padding: 0 18px;
  border-radius: 0 6px 6px 0;
  cursor: pointer;
  transition: background 0.15s ease;
}
.doc-search-btn:hover {
  background: #a93226;
}

/* My Machines / All Documents: a segmented pill toggle, mirroring the
   My Company / All Companies toggle on SavedCarts. */
.doc-toggle {
  display: inline-flex;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}
.doc-toggle-btn {
  padding: 9px 18px;
  background: #fff;
  border: none;
  font-size: 13px;
  font-weight: 600;
  color: #777;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.doc-toggle-btn + .doc-toggle-btn {
  border-left: 1px solid #e0e0e0;
}
.doc-toggle-btn.is-active {
  background: #c0392b;
  color: #fff;
}
.doc-toggle-btn:not(.is-active):hover {
  background: #faf2f1;
  color: #c0392b;
}

/* View PDF: an icon + text link instead of a chunky dark button, matching
   the SavedCarts Review / Create Order action links. */
.doc-pdf-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #333;
  font-weight: 600;
  font-size: 13px;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
}
.doc-pdf-link:hover {
  color: #c0392b;
  text-decoration: underline;
}
.cart-icon-svg {
  vertical-align: middle;
  flex-shrink: 0;
}

/* ===== EXPANDABLE "WHY SUGGESTED" ROW ===== */
/* Round +/- toggle in the first column (mirrors the SavedCarts expander).
   Only rendered for rows that have match reasons. */
.doc-expand-th,
.doc-expand-td {
  width: 34px;
  text-align: center;
  padding-left: 6px !important;
  padding-right: 2px !important;
}
.doc-expand-btn {
  width: 22px;
  height: 22px;
  border: 1px solid #ddd;
  border-radius: 50%;
  background: #fff;
  color: #c0392b;
  font-size: 15px;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}
.doc-expand-btn:hover,
.doc-expand-btn.is-open {
  background: #c0392b;
  color: #fff;
  border-color: #c0392b;
}

/* Detail panel revealed under the row: a light card with a red left rule,
   listing each matched machine / part and when it was purchased. */
.doc-why {
  background: #faf7f6;
  border-left: 3px solid #c0392b;
  border-radius: 4px;
  padding: 12px 16px;
  margin: 4px 0 4px 8px;
}
.doc-why-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #c0392b;
  margin-bottom: 8px;
}
.doc-why-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.doc-why-item {
  font-size: 13px;
  color: #444;
  padding: 3px 0;
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}
.doc-why-kind {
  display: inline-block;
  min-width: 62px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #fff;
  background: #888;
  border-radius: 3px;
  padding: 2px 7px;
  text-align: center;
}
.doc-why-kind.kind-part {
  background: #c0392b;
}
.doc-why-kind.kind-machine {
  background: #2a6a9e;
}
.doc-why-kind.kind-assembly {
  background: #6a8f3a;
}
.doc-why-label {
  font-weight: 600;
  color: #2a2a2a;
}
.doc-why-date {
  color: #777;
}
.doc-why-onfile {
  font-style: italic;
}
/* Documents Page Pagination Fix */
#Documents .page-link {
  color: #000 !important;
}
#Documents .page-link:hover {
  color: #000 !important;
}
#Documents .page-item.active .page-link {
  background-color: #ed1d24;
  border-color: #ed1d24;
  color: #fff !important;
}
#Documents .page-item.disabled .page-link {
  color: #999 !important;
}
</style>